import { CanvasPreview } from "@/utils/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface User {
  name: string;
  email: string;
  id: string;
  picture: string;
  is_selected: boolean;
}

interface ProjectCache {
  canvasPreviews?: CanvasPreview[];
  canvasPreviewsTimestamp?: number;
  assignedUsers?: User[];
}

/**
 * Interface representing the state and methods for managing project cache data
 */
interface ProjectCacheState {
  /** Map of project IDs to their last accessed timestamps */
  projectLastAccessed: { [projectId: string]: number };
  /** Map of project IDs to their cached data */
  projectCacheById: { [projectId: string]: ProjectCache };

  /**
   * Records when a project was last accessed
   * @param projectId - The ID of the project being accessed
   */
  recordProjectAccess: (projectId: string) => void;

  /**
   * Gets a list of recently accessed project IDs, sorted by access time
   * @returns Array of project IDs, most recent first
   */
  getRecentlyAccessedProjectIds: () => string[];

  /**
   * Updates the canvas preview cache for a project
   * @param projectId - The ID of the project to update
   * @param canvasPreviews - Optional array of canvas previews to cache
   */
  setProjectCanvasCache: (
    projectId: string,
    canvasPreviews?: CanvasPreview[]
  ) => void;

  /**
   * Retrieves cached canvas data for a project
   * @param projectId - The ID of the project to get cache for
   * @returns The cached project data or null if not found
   */
  getProjectCanvasCache: (projectId: string) => ProjectCache | null;

  /**
   * Updates the cached users for a project
   * @param projectId - The ID of the project to update
   * @param users - Array of users to cache
   */
  setProjectUsers: (projectId: string, users: User[]) => void;

  /**
   * Retrieves cached users for a project
   * @param projectId - The ID of the project to get users for
   * @returns Array of users or undefined if not found
   */
  getProjectUsers: (projectId: string) => User[] | undefined;
}

export const MAX_RECENT_PROJECTS = 5;

/**
 * store for managing project-related data that needs to persist across sessions.
 * This includes:
 * - Timestamps of when projects were last accessed, to display a list of recently accessed projects.
 * - Cached canvas previews and assigned users for projects.
 * All cached data per project is capped at `MAX_RECENT_PROJECTS` based on last access time.
 */
export const useProjectCacheStore = create<ProjectCacheState>()(
  persist(
    (set, get) => {
      const capProjectCache = (
        currentCache: { [projectId: string]: ProjectCache },
        projectLastAccessed: { [projectId: string]: number }
      ): { [projectId: string]: ProjectCache } => {
        const projectIdsInCache = Object.keys(currentCache);
        if (projectIdsInCache.length <= MAX_RECENT_PROJECTS) {
          return currentCache;
        }

        let oldestProjectIdToRemove: string | null = null;
        let minTimestamp = Infinity;

        for (const id of projectIdsInCache) {
          if (!projectLastAccessed.hasOwnProperty(id)) {
            oldestProjectIdToRemove = id;
            break;
          }
          if (projectLastAccessed[id] < minTimestamp) {
            minTimestamp = projectLastAccessed[id];
            oldestProjectIdToRemove = id;
          }
        }

        if (oldestProjectIdToRemove) {
          const newCache = { ...currentCache };
          delete newCache[oldestProjectIdToRemove];
          return newCache;
        }
        return currentCache;
      };

      return {
        projectLastAccessed: {},
        projectCacheById: {},
        recordProjectAccess: (projectId: string) => {
          set((state) => {
            const newProjectLastAccessed = {
              ...state.projectLastAccessed,
              [projectId]: Date.now(),
            };

            let projectIdsInMap = Object.keys(newProjectLastAccessed);
            if (projectIdsInMap.length > MAX_RECENT_PROJECTS) {
              projectIdsInMap.sort(
                (a, b) => newProjectLastAccessed[a] - newProjectLastAccessed[b]
              );
              const oldestProjectId = projectIdsInMap[0];
              delete newProjectLastAccessed[oldestProjectId];
            }

            let updatedProjectCache = { ...state.projectCacheById };
            const cacheKeys = Object.keys(updatedProjectCache);
            for (const key of cacheKeys) {
              if (!newProjectLastAccessed.hasOwnProperty(key)) {
                delete updatedProjectCache[key];
              }
            }

            return {
              projectLastAccessed: newProjectLastAccessed,
              projectCacheById: updatedProjectCache,
            };
          });
        },

        getRecentlyAccessedProjectIds: () => {
          const { projectLastAccessed } = get();
          const validProjects = Object.keys(projectLastAccessed);
          return validProjects
            .sort(
              (a, b) =>
                (projectLastAccessed[b] || 0) - (projectLastAccessed[a] || 0)
            )
            .slice(0, MAX_RECENT_PROJECTS);
        },

        setProjectCanvasCache: (
          projectId: string,
          canvasPreviews?: CanvasPreview[]
        ) => {
          set((state) => {
            const currentProjectSpecificCache =
              state.projectCacheById[projectId] || {};
            const updatedProjectSpecificData = {
              ...currentProjectSpecificCache,
              canvasPreviews: canvasPreviews,
              canvasPreviewsTimestamp: Date.now(),
            };

            let newProjectCacheById = {
              ...state.projectCacheById,
              [projectId]: updatedProjectSpecificData,
            };

            newProjectCacheById = capProjectCache(
              newProjectCacheById,
              get().projectLastAccessed
            );

            // Ensure projectLastAccessed includes this project if we are caching its data
            const newProjectLastAccessed = { ...get().projectLastAccessed };
            if (!newProjectLastAccessed.hasOwnProperty(projectId)) {
              newProjectLastAccessed[projectId] = Date.now();
            }

            return {
              projectCacheById: newProjectCacheById,
            };
          });
        },

        getProjectCanvasCache: (projectId: string) => {
          const cacheEntry = get().projectCacheById[projectId];
          if (cacheEntry) {
            return {
              canvasPreviews: cacheEntry.canvasPreviews,
              timestamp: cacheEntry.canvasPreviewsTimestamp,
            };
          }
          return null;
        },

        setProjectUsers: (projectId, users) =>
          set((state) => {
            const currentProjectSpecificCache =
              state.projectCacheById[projectId] || {};
            const updatedProjectSpecificData = {
              ...currentProjectSpecificCache,
              assignedUsers: users,
            };
            let newProjectCacheById = {
              ...state.projectCacheById,
              [projectId]: updatedProjectSpecificData,
            };

            newProjectCacheById = capProjectCache(
              newProjectCacheById,
              get().projectLastAccessed
            );

            const newProjectLastAccessed = { ...get().projectLastAccessed };
            if (!newProjectLastAccessed.hasOwnProperty(projectId)) {
              newProjectLastAccessed[projectId] = Date.now();
            }

            return {
              projectCacheById: newProjectCacheById,
            };
          }),

        getProjectUsers: (projectId: string) => {
          return get().projectCacheById[projectId]?.assignedUsers;
        },
      };
    },
    {
      name: "project-data-storage",
    }
  )
);
